// src/belediye/pages/halkMasasi/HalkMasasiApplicationDetailPage.js
//
// Halk Masası — Başvuru Detay (KVKK safe)   FEED UYUMLU
// - Tek kaynak: /halk-masasi/applications/:id/feed/ (timeline + sms + citizen + application)
// - Sol panel: Saha sayfasındaki kart kombinasyonu ile BİREBİR (DOKUNMADIK)
// - Üst başlık: kartların üstüne binmeyecek şekilde “normal akışta” (sticky değil)
// - Sağ panel: MaskedTimeline (kronolojik), soldaki kartlarla aynı hizada başlar
// - KVKK: actor/personel isimleri yok
//
// Route: /yonetim/halk-masasi/basvuru/:applicationId

import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowPathIcon,
  ChevronUpDownIcon,
  UserIcon,
  DocumentTextIcon,
  MapPinIcon,
  PaperClipIcon,
  PhotoIcon,
  Square2StackIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon, //   sağ kart header ikonu
  PlusIcon, //   yeni başvuru butonu ikonu
} from "@heroicons/react/24/outline";

import MaskedTimeline from "../../components/halkMasasi/MaskedTimeline";

import {
  fetchApplicationFeedThunk,
  clearApplicationDetailState,
  selectApplicationDetailLoading,
  selectApplicationDetailError,
  selectApplicationFeedItems,
  selectApplicationDetailState, //   raw feed response (citizen + application)
} from "../../redux/slices/belediyeHalkMasasiSlice";

import TrDateTime from "../../components/utils/TrDateTime";
// FAZ-88: detay başlığını navbara bildir
import { useNavbarBasligiAyarla } from "../../components/NavbarBaslikContext";
import { safeText as safeTextTr, titleCaseTr, ensureFirstUpperTr } from "../../components/utils/textFormat";

import "./HalkMasasiApplicationDetailPage.css";
import "./HalkMasasiApplicationDetailPage.mobile.css";

const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => DEBUG && console.log("[HalkMasasiApplicationDetailPage]", ...args);
const logError = (...args) => DEBUG && console.error("[HalkMasasiApplicationDetailPage]", ...args);

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const toText = (v) => String(v ?? "").trim();

function getFileBase() {
  const env =
    process.env.REACT_APP_FILE_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    "";

  if (env) {
    try {
      return new URL(env, window.location.origin).origin.replace(/\/+$/, "");
    } catch {
      return String(env).replace(/\/+$/, "").replace(/\/api\/?$/, "");
    }
  }
  return window.location.origin.replace(/\/+$/, "");
}

function toAbsoluteUrl(maybeUrl) {
  if (!maybeUrl) return "";
  const u = String(maybeUrl).trim();
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("blob:")) return u;

  const base = getFileBase();
  if (u.startsWith("/")) return `${base}${u}`;
  return `${base}/${u}`;
}

function normalizePhoneForDisplay(raw) {
  const digits = String(raw ?? "").replace(/\D+/g, "");
  if (!digits) return "—";
  if (digits.length === 10) return `0${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return digits;
  if (digits.length === 12 && digits.startsWith("90")) return `0${digits.slice(2)}`;
  return digits;
}

function normalizeUrlList(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const x of list) {
    const abs = toAbsoluteUrl(x);
    if (!abs) continue;
    if (seen.has(abs)) continue;
    seen.add(abs);
    out.push(abs);
  }
  return out;
}

// ------------------------------------------------------------
// UI primitives (Saha kombinasyonu birebir)
// ------------------------------------------------------------
function KeyValueList({ items }) {
  return (
    <dl className="saha-kv">
      {items.map((it) => (
        <div key={it.key} className="saha-kv-row">
          <dt className="saha-kv-key">{it.key}</dt>
          <dd className="saha-kv-val">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function CollapsibleCard({ icon: Icon, title, open, onToggle, children, rightSlot = null }) {
  return (
    <section className="md-card saha-acc">
      <button type="button" className="saha-acc-head" onClick={onToggle} aria-expanded={open ? "true" : "false"}>
        <div className="saha-acc-head-left">
          {Icon ? <Icon className="saha-acc-icon" /> : null}
          <div className="saha-acc-head-text">
            <div className="saha-acc-title">{title}</div>
          </div>
        </div>

        <div className="saha-acc-head-right">
          {rightSlot}
          <ChevronUpDownIcon className={`saha-acc-chevron ${open ? "" : "is-collapsed"}`} />
        </div>
      </button>

      {open ? <div className="saha-acc-body">{children}</div> : null}
    </section>
  );
}

function normalizePhoneForCopy(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `0${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return digits;
  if (digits.length === 12 && digits.startsWith("90")) return `0${digits.slice(2)}`;
  return digits;
}

const PhoneCopyValue = React.memo(function PhoneCopyValue({ phoneDisplay, phoneRaw }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const onCopy = useCallback(async () => {
    const phoneToCopy = normalizePhoneForCopy(phoneRaw || phoneDisplay);
    if (!phoneToCopy) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(phoneToCopy);
      } else {
        const ta = document.createElement("textarea");
        ta.value = phoneToCopy;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      setCopied(true);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // sessiz geç
    }
  }, [phoneRaw, phoneDisplay]);

  if (!phoneDisplay || phoneDisplay === "—") return <span>{phoneDisplay || "—"}</span>;

  return (
    <span className="saha-phone">
      <span className="saha-phone-text">{phoneDisplay}</span>

      <button
        type="button"
        className="btn-md btn-md-ghost saha-copy-btn"
        onClick={onCopy}
        title="Telefonu kopyala"
        aria-label="Telefonu kopyala"
      >
        <Square2StackIcon width={18} height={18} />
      </button>

      {copied ? <span className="saha-copied">Kopyalandı</span> : null}
    </span>
  );
});

// ------------------------------------------------------------
// Page
// ------------------------------------------------------------
export default function HalkMasasiApplicationDetailPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { applicationId } = useParams();

  const effectiveAppId = useMemo(() => String(applicationId || "").trim() || null, [applicationId]);

  const loading = useSelector(selectApplicationDetailLoading);
  const error = useSelector(selectApplicationDetailError);
  const feedItems = useSelector(selectApplicationFeedItems);

  //   raw feed response (citizen + application burada)
  const detailState = useSelector(selectApplicationDetailState);
  const feedRaw = detailState?.applicationDetail?.feed?.raw || detailState?.feed?.raw || null;

  // accordion state (Saha gibi)
  const [openMap, setOpenMap] = useState({
    contact: true,
    app: true,
    attachments: true,
    location: true,
  });
  const toggleOpen = useCallback((k) => setOpenMap((p) => ({ ...p, [k]: !p[k] })), []);

  // Fetch
  useEffect(() => {
    if (!effectiveAppId) {
      logError("missing applicationId param");
      return;
    }

    log("fetchApplicationFeedThunk -> start", { applicationId: effectiveAppId });
    dispatch(fetchApplicationFeedThunk({ applicationId: effectiveAppId }))?.then?.((res) => log("fetch feed result", res));

    return () => {
      dispatch(clearApplicationDetailState());
    };
  }, [dispatch, effectiveAppId]);

  const handleRetry = useCallback(() => {
    if (!effectiveAppId) return;
    dispatch(fetchApplicationFeedThunk({ applicationId: effectiveAppId }));
  }, [dispatch, effectiveAppId]);

  const citizenId = useMemo(() => {
  const c = feedRaw?.citizen || null;
  const id = c?.id ?? c?.citizen_id ?? c?.pk ?? null;
  return id ? String(id) : null;
}, [feedRaw]);

const prefillCitizen = useMemo(() => {
  const c = feedRaw?.citizen || null;
  if (!c || !citizenId) return null;

  return {
    id: citizenId,
    name: c?.name || "",
    surname: c?.surname || "",
    phone_number: c?.phone_number || c?.phone || "",
    email: c?.email || "",
  };
}, [feedRaw, citizenId]);

const handleNewApplication = useCallback(() => {
  if (!citizenId) return;

  navigate(`/yonetim/halk-masasi/vatandas/${citizenId}/yeni-basvuru`, {
    replace: false,
    state: {
      prefillCitizen,
      fromApplicationId: effectiveAppId,
    },
  });
}, [navigate, citizenId, prefillCitizen, effectiveAppId]);

  // ------------------------------------------------------------
  // Extract citizen + application from FEED RAW (your JSON shape)
  // ------------------------------------------------------------
  const extracted = useMemo(() => {
    const c = feedRaw?.citizen || null;
    const a = feedRaw?.application || null;

    // citizen
    const citizenFullName = titleCaseTr(([c?.name, c?.surname].filter(Boolean).join(" ").trim()) || "");
    const citizenPhoneRaw = c?.phone_number || c?.phone || "";
    const citizenPhone = normalizePhoneForDisplay(citizenPhoneRaw);
    const citizenEmail = safeTextTr(c?.email || "");

    // application
    const tracking = toText(a?.tracking_number);
    const unitName = titleCaseTr(toText(a?.unit?.name || ""));
    const neighborhoodName = titleCaseTr(toText(a?.neighborhood?.name || ""));
    const address = ensureFirstUpperTr(toText(a?.address || ""));
    const subject = ensureFirstUpperTr(toText(a?.subject || ""));
    const description = ensureFirstUpperTr(toText(a?.description || ""));

    const createdAt = a?.created_at || null;

    const latN = Number(a?.latitude);
    const lngN = Number(a?.longitude);
    const hasLocation = Number.isFinite(latN) && Number.isFinite(lngN);

    //   attachments: backend JSON has images[] + documents[]
    const images = normalizeUrlList(a?.images || []);
    const documents = normalizeUrlList(a?.documents || []);

    //   assisted intake note (ileride kesin gelecek)
    const assistedNoteRaw = toText(a?.assisted_intake_note || a?.assistedIntakeNote || "");
    const assistedNote = assistedNoteRaw ? ensureFirstUpperTr(assistedNoteRaw) : "";
    //   saat/tarih gösterilmeyecek (kullanıcı isteği)
    // const assistedAt = a?.assisted_intake_created_at || a?.assistedIntakeCreatedAt || null;

    return {
      citizenFullName: citizenFullName || "—",
      citizenPhone,
      citizenPhoneRaw: citizenPhoneRaw || "",
      citizenEmail: citizenEmail || "—",

      tracking: tracking || "—",
      unitName: unitName || "—",
      neighborhoodName: neighborhoodName || "—",
      address: address || "—",
      subject: subject || "—",
      description: description || "—",
      createdAt,

      hasLocation,
      lat: hasLocation ? latN : null,
      lng: hasLocation ? lngN : null,

      images,
      documents,

      assistedNote,
    };
  }, [feedRaw]);

  const headerTitle = useMemo(() => "Başvuru Detayı", []);

  /* FAZ-88 — dar katmanda navbarın orta yuvası konuyu ve takip numarasını
     yazar, sol yuvası geri okuna döner. */
  useNavbarBasligiAyarla(
    feedRaw?.application
      ? {
          ad: extracted.subject !== "—" ? extracted.subject : "Başvuru",
          no: extracted.tracking !== "—" ? extracted.tracking : "",
        }
      : null,
  );

  const showEmptyFeed = useMemo(() => {
    return !loading && (!Array.isArray(feedItems) || feedItems.length === 0);
  }, [loading, feedItems]);

  // maps urls (Saha gibi)
  const mapEmbedSrc = useMemo(() => {
    if (!extracted.hasLocation) return "";
    return `https://maps.google.com/maps?q=${extracted.lat},${extracted.lng}&z=16&output=embed`;
  }, [extracted.hasLocation, extracted.lat, extracted.lng]);

  const mapDirectionsHref = useMemo(() => {
    if (!extracted.hasLocation) return "";
    return `https://maps.google.com/maps?daddr=${extracted.lat},${extracted.lng}`;
  }, [extracted.hasLocation, extracted.lat, extracted.lng]);

  return (
    <div className="hm-page hm-app-detail">
      <div className="hm-shell hm-shell--full hm-appdetail-shell">
        {/* PAGEBAR (NORMAL FLOW) */}
        <div className="hm-ad-pagebar hm-ad-pagebar--static">
          <div className="hm-ad-pagebar-left">
            <div className="hm-ad-pagebar-title">{headerTitle}</div>
          </div>

          <div className="hm-ad-pagebar-actions">
           <button
  type="button"
  className="btn-md btn-md-primary hm-pagebar-primary"
  onClick={handleNewApplication}
  disabled={!citizenId}
  title={!citizenId ? "Vatandaş bilgisi yok" : "Yeni başvuru oluştur"}
>
  <PlusIcon width={18} height={18} />
   Yeni Başvuru
</button>

            <button
              type="button"
              className="btn-md btn-md-ghost"
              onClick={handleRetry}
              disabled={!effectiveAppId || loading}
              title="Yenile"
            >
              <ArrowPathIcon width={18} height={18} />
              Yenile
            </button>
          </div>
        </div>

        {/* Error */}
        {error ? (
          <div className="hm-ad-alert hm-ad-alert--error" role="alert">
            <div className="hm-ad-alert-row">
              <ExclamationTriangleIcon width={18} height={18} />
              <div className="hm-ad-alert-body">
                <div className="hm-ad-alert-title">Veri alınamadı</div>
                <div className="hm-ad-alert-text">{String(error)}</div>
              </div>
            </div>

            <div className="hm-ad-alert-actions">
              <button type="button" className="btn-md btn-md-ghost" onClick={handleRetry} disabled={!effectiveAppId || loading}>
                Tekrar Dene
              </button>
            </div>
          </div>
        ) : null}

        {/* GRID (35 / 65) */}
        <div className="hm-ad-grid hm-ad-grid--3565">
          {/* LEFT (Saha kombinasyonu birebir) */}
          <aside className="hm-ad-left">
            {/* 1) İletişim bilgileri */}
            <CollapsibleCard icon={UserIcon} title="İletişim bilgileri" open={openMap.contact} onToggle={() => toggleOpen("contact")}>
              <KeyValueList
                items={[
                  { key: "Başvuru Sahibi", value: <span className="saha-wrap">{extracted.citizenFullName}</span> },
                  {
                    key: "Telefon Numarası",
                    value: <PhoneCopyValue phoneDisplay={extracted.citizenPhone} phoneRaw={extracted.citizenPhoneRaw} />,
                  },
                  { key: "E-posta", value: <span className="saha-wrap">{extracted.citizenEmail}</span> },
                  { key: "Takip No", value: <span className="saha-wrap">{extracted.tracking}</span> },
                ]}
              />
            </CollapsibleCard>

            {/* 2) Başvuru detayları */}
            <CollapsibleCard icon={DocumentTextIcon} title="Başvuru Detayları" open={openMap.app} onToggle={() => toggleOpen("app")}>
              <KeyValueList
                items={[
                  { key: "Konu", value: <span className="saha-wrap">{extracted.subject}</span> },
                  {
                    key: "Açıklama",
                    value: (
                      <span className="saha-wrap" style={{ whiteSpace: "pre-wrap" }}>
                        {extracted.description}
                      </span>
                    ),
                  },
                  { key: "Birim", value: <span className="saha-wrap">{extracted.unitName}</span> },
                  { key: "Mahalle", value: <span className="saha-wrap">{extracted.neighborhoodName}</span> },
                  { key: "Adres", value: <span className="saha-wrap">{extracted.address}</span> },
                  { key: "Başvuru Tarihi", value: extracted.createdAt ? <TrDateTime value={extracted.createdAt} /> : "—" },

                  ...(extracted.assistedNote
                    ? [
                        {
                          key: "Halk Masası Notu",
                          value: (
                            <span className="saha-wrap" style={{ whiteSpace: "pre-wrap" }}>
                              {extracted.assistedNote}
                            </span>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </CollapsibleCard>

            {/* 3) Ek Dosyalar */}
            {extracted.images.length || extracted.documents.length ? (
              <CollapsibleCard
                icon={PaperClipIcon}
                title="Ek Dosyalar"
                open={openMap.attachments}
                onToggle={() => toggleOpen("attachments")}
                rightSlot={<span className="hm-ad-count">{extracted.images.length + extracted.documents.length}</span>}
              >
                {extracted.images.length ? (
                  <div className="saha-attachments">
                    <div className="saha-attachments-title">
                      <PhotoIcon width={18} height={18} />
                      Görseller
                    </div>
                    <div className="saha-thumb-grid">
                      {extracted.images.map((url, idx) => (
                        <a key={`${url}-${idx}`} className="saha-thumb" href={url} target="_blank" rel="noreferrer" title="Görseli aç">
                          <img src={url} alt={`Görsel ${idx + 1}`} loading="lazy" />
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}

                {extracted.documents.length ? (
                  <div className="saha-docs">
                    <div className="saha-attachments-title" style={{ marginTop: extracted.images.length ? 14 : 0 }}>
                      <DocumentTextIcon width={18} height={18} />
                      Belgeler
                    </div>
                    <div className="saha-doc-grid">
                      {extracted.documents.map((url, idx) => (
                        <a key={`${url}-${idx}`} className="saha-doc-card" href={url} target="_blank" rel="noreferrer" title="Belgeyi aç">
                          <div className="saha-doc-icon">📄</div>
                          <div className="saha-doc-name">Belge {idx + 1}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}
              </CollapsibleCard>
            ) : null}

            {/* 4) Konum */}
            {extracted.hasLocation ? (
              <CollapsibleCard icon={MapPinIcon} title="Konum" open={openMap.location} onToggle={() => toggleOpen("location")}>
                <div className="saha-location">
                  <div className="saha-map-frame">
                    <iframe title="Harita" src={mapEmbedSrc} width="100%" height="240" style={{ border: 0 }} loading="lazy" />
                  </div>

                  <a className="btn-md btn-md-ghost saha-map-cta" href={mapDirectionsHref} target="_blank" rel="noreferrer">
                    <MapPinIcon width={18} height={18} />
                    Google Maps’te yol tarifi al
                  </a>
                </div>
              </CollapsibleCard>
            ) : null}
          </aside>

          {/* RIGHT */}
          <main className="hm-ad-right">
            <section className="md-card hm-ad-timelineCard">
              <div className="hm-ad-timelineHead hm-ad-timelineHead--match">
                <div className="hm-ad-timelineTitleRow">
                  <ChatBubbleLeftRightIcon width={18} height={18} className="hm-ad-timelineTitleIcon" />
                  <div className="hm-ad-timelineTitle">İşlemler + SMS geçmişi</div>
                </div>

                <div className="hm-ad-timelineSub">
                  {loading ? "Yükleniyor…" : `${Array.isArray(feedItems) ? feedItems.length : 0} kayıt`}
                </div>
              </div>

              <MaskedTimeline items={Array.isArray(feedItems) ? feedItems : []} loading={loading} resetKey={effectiveAppId || ""} />

              {showEmptyFeed ? (
                <div className="hm-ad-empty" style={{ marginTop: 10 }}>
                  Bu başvuru için görüntülenebilir kayıt bulunamadı.
                </div>
              ) : null}
            </section>
          </main>
        </div>

        <div className="hm-bottom-space" />
      </div>
    </div>
  );
}